<template>
    <div class="w-full flex flex-col p-8 text-ternary text-left">
        <div class="bg-white shadow rounded-xl p-6 space-y-4 text-sm text-gray-800">
            <h2 class="text-xl font-semibold border-b pb-2">📄 Job Details</h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><span class="font-medium text-gray-600">Employer:</span> {{ item.partner ?
                    item.partner.en_company_name : item.employer_name }}</div>
                <div><span class="font-medium text-gray-600">Job Title:</span> {{ item.job_title }}</div>
                <div><span class="font-medium text-gray-600">Position:</span> {{ item.position_name }}</div>
                <div><span class="font-medium text-gray-600">Position Code:</span> {{ item.position_number }}</div>
                <div><span class="font-medium text-gray-600">Grade Number:</span> {{ item.grade_number }}</div>
                <div><span class="font-medium text-gray-600">Job Location:</span> {{ item.job_location }}</div>
                <div><span class="font-medium text-gray-600">Minimum Qualification:</span> {{ item.minimum_qualification
                }}</div>
                <div><span class="font-medium text-gray-600">Preferred Qualification:</span> {{
                    item.preferred_qualification }}</div>
                <div><span class="font-medium text-gray-600">Expected Skills/Rank/Experience:</span> {{
                    item.expected_skills }}</div>
                <div><span class="font-medium text-gray-600">Vacancy:</span> {{ item.vacancy }}</div>
                <div><span class="font-medium text-gray-600">Benefits:</span> {{ item.benefits }}</div>
                <div><span class="font-medium text-gray-600">Working Hours:</span> {{ item.working_hours }}</div>
                <div><span class="font-medium text-gray-600">Contract Type:</span> {{ item.contract_type }}</div>
                <div><span class="font-medium text-gray-600">Division:</span> {{ item.division }}</div>
                <div><span class="font-medium text-gray-600">Department:</span> {{ item.department }}</div>
                <div><span class="font-medium text-gray-600">Job Start Date:</span> {{
                    formattedDate(item.job_start_date) }}</div>
                <div><span class="font-medium text-gray-600">Job Close Date:</span> {{
                    formattedDate(item.job_close_date) }}</div>
                <div><span class="font-medium text-gray-600">Job Category:</span> {{ item.job_category }}</div>
                <div v-if="userType != 'student'"><span class="font-medium text-gray-600">Salary:</span> {{ item.salary
                }}</div>
            </div>

            <div>
                <p class="font-medium text-gray-600 mb-1">Job Description:</p>
                <div class="text-gray-700 whitespace-pre-line">{{ item.job_description }}</div>
            </div>

            <div>
                <p class="font-medium text-gray-600 mb-1">Special Instructions to Applicant:</p>
                <div class="text-gray-700 whitespace-pre-line">{{ item.special_instructions }}</div>
            </div>
        </div>

        <div class="mt-4 p-6 bg-white rounded-2xl shadow-md border border-gray-200">
            <h2 class="text-xl font-semibold text-gray-800 border-b pb-3 mb-4">🎯 Target Audience</h2>
            <div class="space-y-4 text-sm text-gray-700">
                <div class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Entity</div>
                    <div class="w-full sm:w-3/4">{{ getEntityName(item.entity) }}</div>
                </div>
                <div v-if="item.campus && item.campus.length > 0" class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Campus</div>
                    <div class="w-full sm:w-3/4">{{ item.campus.join(', ') }}</div>
                </div>
                <div v-if="item.year_of_study && item.year_of_study.length > 0" class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">
                        {{ item.entity == 'ATHS' ? 'Grade' : 'Year of Study' }}
                    </div>
                    <div class="w-full sm:w-3/4">{{ item.year_of_study.join(', ') }}</div>
                </div>
                <div class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Program</div>
                    <div class="w-full sm:w-3/4">{{ item.program ? item.program : 'All' }}</div>
                </div>
                <div class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Student Status</div>
                    <div class="w-full sm:w-3/4">{{ item.student_status }}</div>
                </div>
                <div class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Nationality</div>
                    <div class="w-full sm:w-3/4">{{ getJobFor(item.job_for) }}</div>
                </div>
                <div class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Gender</div>
                    <div class="w-full sm:w-3/4">{{ getGender(item.gender) }}</div>
                </div>
            </div>
        </div>

        <!-- Student View -->
        <div v-if="userType === 'student'" class="flex justify-end mt-4">
            <button @click.stop="applyJob" class="px-6 py-2 rounded-lg text-sm font-semibold uppercase transition-all duration-200
           border-2 border-primary bg-primary text-white hover:bg-white hover:text-primary shadow-sm">
                Apply
            </button>
        </div>

        <!-- Admin/Employer View -->
        <div v-else class="mt-4 p-6 bg-white rounded-2xl shadow-md border border-gray-200">
            <h3 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">📊 Application Summary</h3>

            <div class="space-y-4 text-sm text-gray-700">
                <div class="flex flex-wrap">
                    <div class="w-full sm:w-1/4 font-medium text-gray-600">Total Applications</div>
                    <div class="w-full sm:w-3/4">{{ item.request_count }}</div>
                </div>

                <div class="flex flex-wrap text-green-600">
                    <div class="w-full sm:w-1/4 font-medium">Accepted</div>
                    <div class="w-full sm:w-3/4">{{ item.approved_count }}</div>
                </div>

                <div class="flex flex-wrap text-red-500">
                    <div class="w-full sm:w-1/4 font-medium">Rejected</div>
                    <div class="w-full sm:w-3/4">{{ item.rejected_count }}</div>
                </div>

                <div class="flex flex-wrap text-yellow-500">
                    <div class="w-full sm:w-1/4 font-medium">Not Verified Yet</div>
                    <div class="w-full sm:w-3/4">
                        {{ item.request_count - (item.approved_count + item.rejected_count) }}
                    </div>
                </div>
            </div>
        </div>

        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
export default {
    name: "apply-job",
    props: ["item", "userType"],
    emits: ["close"],
    data() {
        return {
            isLoading: false,
            error: null,
            success: null,
        }
    },
    methods: {
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        formattedDate(date) {
            if (date) {
                const parsedDate = new Date(date);
                return parsedDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).replace(/\//g, '-');
            } else {
                return "";
            }
        },
        async applyJob() {
            if (this.userType === 'student') {
                this.isLoading = true;

                const CREATE_MUTATION = gql`
                    mutation ApplyJob($job_id: Int!) {
                        applyJob(job_id: $job_id) {
                            error
                            error_code
                            message
                        }
                    }
                `;

                let responseData;

                try {
                    await this.$apollo.mutate({
                        mutation: CREATE_MUTATION,
                        variables: {
                            job_id: parseInt(this.item.id),
                        }
                    }).then((data) => {
                        responseData = data.data.applyJob;
                        if (data.data.applyJob.error) {
                            this.error = data.data.applyJob.message;
                        } else {
                            this.success = data.data.applyJob.message;
                        }
                    }).catch((error) => {
                        console.log(error)
                        this.error = error.message;
                    })

                } catch (error) {
                    console.log(error);
                    this.error = error.message || "Please try again later.";
                }

                if (this.error === null) {

                    const Toast = this.$swal.mixin({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 1000,
                        timerProgressBar: true,
                        didOpen: (toast) => {
                            toast.addEventListener('mouseenter', this.$swal.stopTimer)
                            toast.addEventListener('mouseleave', this.$swal.resumeTimer)
                        }
                    })
                    Toast.fire({
                        icon: 'success',
                        title: this.success
                    })

                    this.$emit('close');

                } else {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: this.error,
                    }).then((result) => {
                        if (result.value || result.dismiss) {
                            if (responseData.error_code == 405) {
                                window.location.reload();
                            } else {
                                this.$emit('close');
                            }
                        }
                    })
                }

                this.isLoading = false;
            }
        },
        getJobFor(val) {
            if (val == 1) {
                return "Local";
            } else if (val == 2) {
                return "Non Local";
            } else if (val == 3) {
                return "Both Local and Non Local";
            } else {
                return "";
            }
        },
        getGender(val) {
            if (val) {
                if (val == 'M') {
                    return 'Male';
                } else {
                    return 'Female';
                }
            } else {
                return "Both Male & Female";
            }
        },
    },
}
</script>